import styled from '@emotion/styled';
import { graphql, useStaticQuery } from 'gatsby';
import BackgroundImage from 'gatsby-background-image';
import { getImage } from 'gatsby-plugin-image';
import { convertToBgImage } from 'gbimage-bridge';
import React from 'react';
import $ from 'text2vdom';

import ContentBox from '~/components/ContentBox';
import MobileLineBreak from '~/components/MobileLineBreak';
import { css } from '~/css';
import type { BackgroundImageQuery } from '~/generated/types/gatsby';
import { MOBILE_SCREEN_WIDTH, PC_SCREEN_WIDTH } from '~/constants/screen';

import SectionBase, { SectionBaseProps } from './base';

export interface BackgroundImageSectionProps extends SectionBaseProps {
  description: string;
  image: {
    pc: string;
    mobile: string;
  };
}

interface BackgroundImageProps {
  id?: string; // workaround. see: https://github.com/timhagn/gatsby-background-image/pull/77
  minHeight: number;
}

const PcBackgroundImage = styled(BackgroundImage)((props: BackgroundImageProps) => {
  const size = props.minHeight;
  return css({
    display: ['none', 'block'],
    minHeight: `${size}px`,
    height: `${(size / PC_SCREEN_WIDTH) * 100}vw`,
  })(props);
});

const MobileBackgroundImage = styled(BackgroundImage)((props: BackgroundImageProps) => {
  const size = props.minHeight;
  return css({
    display: ['block', 'none'],
    minHeight: `${size}px`,
    height: `${(size / MOBILE_SCREEN_WIDTH) * 100}vw`,
  })(props);
});

const Description = styled('p')(
  css({
    textAlign: 'center',
    whiteSpace: 'pre-line',
    fontSize: [16, 18],
    lineHeight: 1.5,
    textShadow: '0 0 0.5em #fff',
    margin: 0,
    marginTop: [8, '8px'],
    '> br': {
      display: ['block', 'none'],
    },
  }),
);

const BackgroundImageSection: React.FC<BackgroundImageSectionProps> = ({ id, title, description, image }) => {
  const data = useStaticQuery<BackgroundImageQuery>(graphql`
    query BackgroundImage {
      allPcBackgroundImage: allFile(filter: { relativePath: { regex: "//background/.*pc/" } }) {
        nodes {
          relativePath
          childImageSharp {
            gatsbyImageData(width: 1920, breakpoints: [640, 1200], formats: [AUTO, WEBP])
          }
        }
      }
      allMobileBackgroundImage: allFile(filter: { relativePath: { regex: "//background/.*mobile/" } }) {
        nodes {
          relativePath
          childImageSharp {
            gatsbyImageData(width: 360, breakpoints: [360, 640], formats: [AUTO, WEBP])
          }
        }
      }
    }
  `);

  const pcBgNode = data.allPcBackgroundImage.nodes.find((node) => node.relativePath === image.pc);
  const mobileBgNode = data.allMobileBackgroundImage.nodes.find((node) => node.relativePath === image.mobile);

  const mbr = () => <MobileLineBreak />;
  const Content = (
    <SectionBase Tag='div' title={title}>
      <ContentBox>
        <Description>{$(description, { mbr })}</Description>
      </ContentBox>
    </SectionBase>
  );

  return (
    <>
      {pcBgNode && (
        <PcBackgroundImage
          id={id}
          Tag='section'
          {...convertToBgImage(getImage(pcBgNode))}
          minHeight={pcBgNode.childImageSharp.gatsbyImageData.height}
        >
          {Content}
        </PcBackgroundImage>
      )}
      {mobileBgNode && (
        <MobileBackgroundImage
          id={id}
          Tag='section'
          {...convertToBgImage(getImage(mobileBgNode))}
          minHeight={mobileBgNode.childImageSharp.gatsbyImageData.height}
        >
          {Content}
        </MobileBackgroundImage>
      )}
    </>
  );
};

export default BackgroundImageSection;
